/**
 * Marca de sessão ativa. NÃO é credencial.
 *
 * O JWT saiu do localStorage e passou a viver num cookie HttpOnly (RG-05), que
 * o JavaScript não consegue ler — é o que impede um XSS de roubar a sessão.
 * Sobra aqui apenas um "sim, havia sessão", para a interface decidir no boot se
 * tenta restaurar ou manda para o login.
 */
export const AUTH_SESSION_KEY = 'authSessaoAtiva';

/** Chave herdada, onde o token ficava. Existe só para apagar o resíduo. */
const AUTH_TOKEN_KEY_LEGADO = 'authToken';

export const AUTH_USER_KEY = 'authUser';
export const AUTH_USER_CACHE_KEY = 'authUserCache';
export const AUTH_LAST_LOGIN_AT_KEY = 'authLastLoginAt';
export const AUTH_OFFLINE_ALLOWED_UNTIL_KEY = 'authOfflineAllowedUntil';
export const AUTH_RELOGIN_MESSAGE_KEY = 'authReloginMessage';
export const LAST_APP_ROUTE_KEY = 'lastAppRoute';
export const PROJETO_ATIVO_KEY = 'projetoAtivo';

const OFFLINE_SESSION_DAYS = 15;
const DAY_IN_MS = 24 * 60 * 60 * 1000;

const RESUMABLE_ROUTE_PREFIXES = [
  '/dashboard',
  '/estatisticas',
  '/apreciacoes',
  '/meus-templates',
  '/modec',
  '/manual-operacao',
  '/manuais',
  '/cronograma',
  '/aquisicao-campo',
  '/relatorio',
  '/perfil',
  '/projeto-selecao',
  '/admin',
  '/portal',
];

const MARKETING_ROUTE_PREFIXES = [
  '/',
  '/login',
];

export function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const touchAppleDesktop = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua) || touchAppleDesktop;
}

export function buildOfflineWindow(now = new Date()): { lastLoginAt: string; offlineAllowedUntil: string } {
  const lastLoginAt = now.toISOString();
  const offlineAllowedUntil = new Date(now.getTime() + OFFLINE_SESSION_DAYS * DAY_IN_MS).toISOString();
  return { lastLoginAt, offlineAllowedUntil };
}

export function isOfflineWindowValid(offlineAllowedUntil?: string | null, referenceDate = new Date()): boolean {
  if (!offlineAllowedUntil) return false;
  const limit = new Date(offlineAllowedUntil);
  if (Number.isNaN(limit.getTime())) return false;
  return limit.getTime() >= referenceDate.getTime();
}

function extractPathname(route: string): string {
  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://riskguard.com.br';
    return new URL(route, origin).pathname;
  } catch {
    return route.split('?')[0].split('#')[0];
  }
}

export function isPersistableAppRoute(route: string): boolean {
  if (!route || !route.startsWith('/')) return false;
  const pathname = extractPathname(route);
  return RESUMABLE_ROUTE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

export function saveLastAppRoute(route: string): void {
  if (typeof localStorage === 'undefined') return;
  if (!isPersistableAppRoute(route)) return;
  localStorage.setItem(LAST_APP_ROUTE_KEY, route);
}

export function getLastAppRoute(): string | null {
  if (typeof localStorage === 'undefined') return null;
  const stored = localStorage.getItem(LAST_APP_ROUTE_KEY);
  if (!stored || !isPersistableAppRoute(stored)) return null;
  return stored;
}

export function resolveAuthenticatedEntryRoute(hasProject: boolean, isAdmin = true): string {
  if (!hasProject) return '/projeto-selecao';
  const stored = getLastAppRoute();
  if (!stored) return '/dashboard';
  const pathname = extractPathname(stored);
  if (!isAdmin && (pathname === '/admin' || pathname.startsWith('/admin/'))) {
    return '/dashboard';
  }
  if (pathname === '/projeto-selecao') return '/dashboard';
  return stored;
}

export function hasStoredProject(): boolean {
  if (typeof localStorage === 'undefined') return false;
  const raw = localStorage.getItem(PROJETO_ATIVO_KEY);
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw) as { id?: string };
    return typeof parsed?.id === 'string' && parsed.id.length > 0;
  } catch {
    return false;
  }
}

export function clearActiveAuthStorage(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(AUTH_SESSION_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
  removerTokenLegado();
}

/**
 * Apaga o JWT que versões anteriores gravavam em localStorage. Roda no boot:
 * enquanto o valor antigo continuar no navegador, o token segue exposto a
 * qualquer script da página, mesmo já não sendo mais usado.
 */
export function removerTokenLegado(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(AUTH_TOKEN_KEY_LEGADO);
}

export function marcarSessaoAtiva(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(AUTH_SESSION_KEY, '1');
}

export function haSessaoMarcada(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(AUTH_SESSION_KEY) === '1';
}

export function clearAllAuthStorage(): void {
  if (typeof localStorage === 'undefined') return;
  clearActiveAuthStorage();
  localStorage.removeItem(AUTH_USER_CACHE_KEY);
  localStorage.removeItem(AUTH_LAST_LOGIN_AT_KEY);
  localStorage.removeItem(AUTH_OFFLINE_ALLOWED_UNTIL_KEY);
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(AUTH_RELOGIN_MESSAGE_KEY);
  }
}

export function isPublicRoute(pathname: string): boolean {
  return MARKETING_ROUTE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}
